import { FC, useState } from 'react';
import classnames from 'classnames';
import { Button, Icon } from 'common-components';
import { getPasswordError, getReEnteredPasswordError } from 'utils';
import { useTranslationContext } from '../contexts/translationContext';
import { ArrowBack, CheckIcon } from '../uiLibraryImports';
import Password from '../password';
import { ChangePasswordProps } from './changePasswordTypes';
import styles from './changePassword.module.scss';

const ChangePassword: FC<ChangePasswordProps> = (props: ChangePasswordProps) => {
    const {
        email,
        nickname,
        onChangePassword,
        onBack,
        isLoading,
        errorResponseMessage,
        hideForgotPassword,
        onClose,
        onForgotPassword,
        imgSrc
    } = props;
    const { translate } = useTranslationContext();
    const [currentPassword, setCurrentPassword] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [newPasswordError, setNewPasswordError] = useState<string>('');
    const [reEnteredPasswordError, setReEnteredPasswordError] = useState<string>('');
    const [reEnteredPass, setReEnteredPass] = useState<string>('');
    const [enteredNewPassword, setEnteredNewPassword] = useState<boolean>(false);

    const validateNewPassword = (): void => {
        if (newPassword) setEnteredNewPassword(true);
        else setEnteredNewPassword(false);

        if (!newPassword) return setNewPasswordError('');

        if (currentPassword === newPassword) return setNewPasswordError('passwordsMustBeDifferent');
        return setNewPasswordError(getPasswordError(newPassword));
    };

    const validateReEnteredPassword = (): void => {
        if (reEnteredPass) return setReEnteredPasswordError(getReEnteredPasswordError(newPassword, reEnteredPass));
        return setReEnteredPasswordError('');
    };

    const isEnabled =
        Boolean(newPassword) && newPassword === reEnteredPass && !reEnteredPasswordError && !newPasswordError;
    const isPasswordMustBeDifferentError = newPasswordError === 'passwordsMustBeDifferent';

    return (
        <div
            className={styles['change-password-wrapper']}
            style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/accountSettings.svg)` }}
        >
            <div className={styles['top-wrapper']}>
                <div className={styles.back} onClick={() => onBack()}>
                    <ArrowBack className={styles['back-arrow']} />
                    <div data-testid="text">{translate('Back')}</div>
                </div>
                <Icon name="cancel" onClick={() => onClose()} className={styles.cancel} />
            </div>
            <div className={styles['user-details']}>
                <img src={imgSrc} className={styles['profile-picture']} alt="profile" />
                <div title={nickname} className={styles.nickname}>
                    {nickname}
                </div>
                <div title={email} className={styles.email}>
                    {email}
                </div>
            </div>
            <div className={styles.inputs}>
                <div className={styles.section} data-testid="current-password">
                    <div className={styles['section-title']}>{translate('CurrentPassword')}</div>
                    <Password
                        password={currentPassword}
                        onChangePassword={setCurrentPassword}
                        error={!!errorResponseMessage}
                        errorMessage={errorResponseMessage}
                        onBlur={validateNewPassword}
                    />
                    {!hideForgotPassword && (
                        <div className={styles['forgot-password']} onClick={() => onForgotPassword?.()}>
                            {translate('forgotPassword')}
                        </div>
                    )}
                </div>
                <div className={styles.section} data-testid="new-password">
                    <div className={styles['section-title']}>{translate('NewPassword')}</div>
                    <Password
                        password={newPassword}
                        onChangePassword={setNewPassword}
                        error={!!newPasswordError}
                        errorMessage={isPasswordMustBeDifferentError && translate(newPasswordError)}
                        onBlur={validateNewPassword}
                    />
                    {!isPasswordMustBeDifferentError && (
                        <>
                            <div
                                className={classnames(styles['password-instructions'], {
                                    [styles['new-password-error']]: newPasswordError
                                })}
                            >
                                {translate('passwordSetIntructions')}
                            </div>
                            {enteredNewPassword && !newPasswordError && <CheckIcon className={styles['check-icon']} />}
                        </>
                    )}
                </div>
                <div className={styles.section} data-testid="re-entered-password">
                    <div className={styles['section-title']}>{translate('ReEnterPassword')}</div>
                    <Password
                        password={reEnteredPass}
                        onChangePassword={setReEnteredPass}
                        error={!!reEnteredPasswordError}
                        errorMessage={translate(reEnteredPasswordError)}
                        onBlur={validateReEnteredPassword}
                    />
                </div>
            </div>
            <div className={styles.submit}>
                <Button
                    dataTestid="change-password-submit-button"
                    type={isEnabled ? 'bigGradient' : 'bigGradientDisable'}
                    onClick={() => onChangePassword(currentPassword, newPassword, reEnteredPass)}
                    text={translate('ChangePassword')}
                    isLoading={isLoading}
                    lowerCase
                />
            </div>
        </div>
    );
};

export default ChangePassword;
